/**
 * Contact details, training locations, business hours and social links.
 * Every value is admin-editable through Public Content → Contact.
 */

import { app } from './app.js';
import { placeholder } from './placeholders.js';

export interface TrainingLocation {
  id: string;
  name: string;
  addressLine1: string;
  addressLine2: string | null;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  /** Free-text notes: parking, entry instructions, equipment available. */
  notes: string | null;
  /** Maximum concurrent attendees; enforced server-side on booking acceptance. */
  capacity: number;
  /** Whether online sessions are delivered from this location. */
  supportsOnline: boolean;
  isPrimary: boolean;
  isActive: boolean;
}

/**
 * Locations are seeded as configurable placeholders and are intended to be
 * replaced by real records via Admin → Settings → Locations.
 */
export const locations: readonly TrainingLocation[] = [
  {
    id: 'primary',
    name: placeholder('PRIMARY LOCATION'),
    addressLine1: placeholder('PRIMARY LOCATION'),
    addressLine2: null,
    suburb: placeholder('PRIMARY LOCATION'),
    state: 'NSW',
    postcode: '0000',
    country: 'Australia',
    notes: null,
    capacity: 6,
    supportsOnline: true,
    isPrimary: true,
    isActive: true,
  },
  {
    id: 'secondary',
    name: placeholder('SECONDARY LOCATION'),
    addressLine1: placeholder('SECONDARY LOCATION'),
    addressLine2: null,
    suburb: placeholder('SECONDARY LOCATION'),
    state: 'NSW',
    postcode: '0000',
    country: 'Australia',
    notes: null,
    capacity: 4,
    supportsOnline: false,
    isPrimary: false,
    isActive: true,
  },
];

export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface BusinessHoursEntry {
  weekday: Weekday;
  label: string;
  /** `null` when closed. Local wall-clock time in the business timezone. */
  opens: string | null;
  closes: string | null;
}

/**
 * Default operating window. These are scaffolding values, not a claim about the
 * client's real availability - the real hours are tracked in
 * docs/CONTENT_REQUIRED.md and set through Admin → Availability, which is what
 * actually governs bookable slots.
 */
export const businessHours: readonly BusinessHoursEntry[] = [
  { weekday: 1, label: 'Monday', opens: '05:30', closes: '19:00' },
  { weekday: 2, label: 'Tuesday', opens: '05:30', closes: '19:00' },
  { weekday: 3, label: 'Wednesday', opens: '05:30', closes: '19:00' },
  { weekday: 4, label: 'Thursday', opens: '05:30', closes: '19:00' },
  { weekday: 5, label: 'Friday', opens: '05:30', closes: '17:00' },
  { weekday: 6, label: 'Saturday', opens: '07:00', closes: '12:00' },
  { weekday: 0, label: 'Sunday', opens: null, closes: null },
];

export interface SocialLink {
  id: string;
  label: string;
  url: string;
  /** Icon key resolved by the UI package. */
  icon: 'instagram' | 'facebook' | 'youtube' | 'tiktok' | 'linkedin' | 'link';
}

/**
 * Social profiles. Entries whose `url` is still a placeholder are filtered out
 * of the rendered footer so the site never shows a dead link.
 */
export const socialLinks: readonly SocialLink[] = [
  { id: 'instagram', label: 'Instagram', url: placeholder('SOCIAL LINKS'), icon: 'instagram' },
  { id: 'facebook', label: 'Facebook', url: placeholder('SOCIAL LINKS'), icon: 'facebook' },
  { id: 'youtube', label: 'YouTube', url: placeholder('SOCIAL LINKS'), icon: 'youtube' },
];

export const contact = {
  email: placeholder('BUSINESS EMAIL'),
  phone: placeholder('BUSINESS PHONE'),
  businessHoursSummary: placeholder('BUSINESS HOURS'),
  publicDomain: placeholder('PUBLIC DOMAIN'),
  apiDomain: placeholder('API DOMAIN'),

  locations,
  businessHours,
  socialLinks,

  timezone: app.timezone,
  currency: app.currency,
  locale: app.locale,

  /** Sender identity for transactional email, configured on the platform. */
  emailSender: {
    name: 'Iron Resolve Combat Fitness',
    address: placeholder('BUSINESS EMAIL'),
    replyTo: placeholder('BUSINESS EMAIL'),
  },
} as const;

export function primaryLocation(): TrainingLocation {
  const found = locations.find((location) => location.isPrimary && location.isActive);
  // `locations` is non-empty by construction, but keep the type honest.
  const fallback = locations[0];
  if (!found && !fallback) throw new Error('No training locations are configured.');
  return found ?? (fallback as TrainingLocation);
}
